import React, { useState } from 'react';
import { Trash2, Users2, Calendar, X, CheckSquare, Edit3, Copy, Zap } from 'lucide-react';
import { DatePicker } from 'antd';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { hasFullAdminAccess } from '../../utils/roleUtils';

interface TaskBulkActionsBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkAction: (
    action: string,
    date?: string,
    assigneeId?: string
  ) => void;
  onEditStatus: () => void;
  onCopyToList: () => void;
  onAutomation: () => void;
}


interface Employee {
  value: string;
  label: string;
}

interface ConfirmationState {
  isOpen: boolean;
  action: string;
  message: string;
  selectedEmployee?: string;
  selectedStatus?: string;
  selectedList?: string;
  selectedAutomation?: string;
}

const ActionBar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 240px; // Account for sidebar
  right: 24px;
  margin: 0 auto;
  width: fit-content;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-radius: 16px;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  border: 1px solid #f0f0f0;
  z-index: 1000;
`;

// ADD this styled component
const DatePickerContainer = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 8px;
  z-index: 1001;
  width: 300px;

  .ant-picker {
    width: 100%;
  }
`;
const SelectionBadge = styled.div`
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  min-width: 100px;
  text-align: center;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-left: 16px;
  border-left: 1px solid #f0f0f0;
`;

const ActionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
  min-width: 64px;

  &:hover {
    background: #f8fafc;
    color: #2563eb;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  span {
    font-size: 12px;
    font-weight: 500;
  }
`;



const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const SelectWrapper = styled.div`
  margin-bottom: 24px;
  select {
    width: 100%;
    height: 40px;
    padding: 8px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
  }
`;

const ModalContent = styled.div`
  position: relative;
  z-index: 1101;
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 400px;
  max-width: 90vw;
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
`;

const ModalMessage = styled.p`
  margin: 0 0 24px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button<{ isWarning?: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.isWarning ? `
    background: #ef4444;
    color: white;
    &:hover { background: #dc2626; }
  ` : `
    background: #f1f5f9;
    color: #64748b;
    &:hover { background: #e2e8f0; }
  `}
`;

const getActionTitle = (action: string): string => {
  switch (action) {
    case 'delete': return 'Delete';
    case 'reassign': return 'Reassign';
    case 'updateDueDate': return 'Update Date';
    case 'complete': return 'Complete';
    case 'editStatus': return 'Edit Status';
    case 'assignToList': return 'Assign to List';
    case 'copyToList': return 'Copy to List';
    case 'automation': return 'Apply Automation';
    default: return 'Confirm';
  }
};

const getConfirmationMessage = (action: string, count: number): string => {
  switch (action) {
    case 'delete':
      return `Are you sure you want to delete ${count} selected tasks? This action cannot be undone.`;
    case 'complete':
      return `Are you sure you want to mark ${count} tasks as complete?`;
    case 'reassign':
      return `Select an employee to reassign ${count} tasks:`;
    case 'editStatus':
      return `Select a status to apply to ${count} selected tasks:`;
    case 'assignToList':
      return `Select a list to assign ${count} tasks to:`;
    case 'copyToList':
      return `Select a list to copy ${count} tasks to:`;
    case 'automation':
      return `Select an automation to apply to ${count} selected tasks:`;
    default:
      return 'Are you sure you want to continue?';
  }
};




const TaskBulkActionsBar: React.FC<TaskBulkActionsBarProps> = ({
  selectedCount,
  onClearSelection,
  onBulkAction,
  onEditStatus,
  onCopyToList,
  onAutomation
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    isOpen: false,
    action: '',
    message: ''
  });


  const handleActionClick = (action: string) => {
    if (action === 'updateDueDate') {
      setShowDatePicker(true);
    } else {
      setConfirmation({
        isOpen: true,
        action,
        message: getConfirmationMessage(action, selectedCount)
      });
    }
  };

  const handleDateChange = (date: moment.Moment | null) => {
    if (date) {
      // Format date as YYYY-MM-DD string without time
      const formattedDate = date.format('YYYY-MM-DD');
      // Call bulk action with formatted date string
      onBulkAction('updateDueDate', formattedDate);
      // Close the date picker
      setShowDatePicker(false);
    }
  };
  

  const formatName = (firstName: string, lastName: string | null) => {
    if (!firstName) return '';
    return lastName ? `${firstName} ${lastName}`.trim() : firstName.trim();
  };

  // 🔒 SECURITY FIX: Use subordinate data with role-based filtering
  const employees: Employee[] = useSelector((state: any) => {
    const userRole = state?.rootReducers?.userData?.userDetails?.role?.name;
    const subordinateData = state?.rootReducers?.userData?.userDetails?.subordinateEmployees;
    const userDetails = state?.rootReducers?.userData?.userDetails;

    // Super admin gets all organization employees
    if (hasFullAdminAccess(userDetails)) {
      const orgEmployees = userDetails?.organizationEmployee || [];
      return orgEmployees.map((item: any) => ({
        value: item?._id,
        label: formatName(item?.firstName, item?.lastName)
      }));
    }

    // Get subordinate arrays
    const reportTo = subordinateData?.reportTo || [];
    const teamMembers = subordinateData?.teamMembers || [];
    const orgMembers = subordinateData?.organizationMember || [];

    let filteredUsers: any[] = [];

    if (userRole === 'employee') {
      // 🔒 EMPLOYEES: Can ONLY see themselves
      const currentUser = {
        firstName: userDetails?.firstName || '',
        lastName: userDetails?.lastName || '',
        _id: userDetails?._id,
        email: userDetails?.email || '',
      };
      filteredUsers = [currentUser];

    } else if (userRole === 'manager' || userRole === 'team_lead') {
      // 🔓 MANAGERS/TEAM LEADS: Can see their subordinates
      const allSubordinates = [...reportTo, ...teamMembers];
      // Remove duplicates
      filteredUsers = allSubordinates.filter((user, index, array) =>
        array.findIndex(u => u._id === user._id) === index
      );

    } else {
      // 🔓 OTHER ROLES: Can see organization members
      const allUsers = [...reportTo, ...teamMembers, ...orgMembers];
      // Remove duplicates
      filteredUsers = allUsers.filter((user, index, array) =>
        array.findIndex(u => u._id === user._id) === index
      );
    }

    return filteredUsers.map((item: any) => ({
      value: item?._id,
      label: formatName(item?.firstName, item?.lastName)
    }));
  });

  // Status options for tasks
  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Mock list options - replace with actual data from your state/API
  const listOptions = [
    { value: 'hot_leads', label: 'Hot Leads' },
    { value: 'cold_leads', label: 'Cold Leads' },
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'converted', label: 'Converted' }
  ];

  // Mock automation options - replace with actual data from your state/API
  const automationOptions = [
    { value: 'welcome_sequence', label: 'Welcome Sequence' },
    { value: 'follow_up_sequence', label: 'Follow Up Sequence' },
    { value: 'nurture_campaign', label: 'Nurture Campaign' },
    { value: 'conversion_flow', label: 'Conversion Flow' }
  ];
  
  const handleConfirm = () => {
    const { action, selectedEmployee, selectedStatus, selectedList, selectedAutomation } = confirmation;

    switch (action) {
      case 'reassign':
        if (selectedEmployee) {
          onBulkAction('reassign', undefined, selectedEmployee);
        }
        break;
      case 'editStatus':
        if (selectedStatus) {
          onBulkAction('editStatus', selectedStatus);
        }
        break;
      case 'copyToList':
        if (selectedList) {
          onBulkAction('copyToList', selectedList);
        }
        break;
      case 'automation':
        if (selectedAutomation) {
          onBulkAction('automation', selectedAutomation);
        }
        break;
      default:
        onBulkAction(action);
        break;
    }

    setConfirmation({
      isOpen: false,
      action: '',
      message: '',
      selectedEmployee: undefined,
      selectedStatus: undefined,
      selectedList: undefined,
      selectedAutomation: undefined
    });
  };

  return (
    <>
      <ActionBar>
        <SelectionBadge>
          {selectedCount} selected
        </SelectionBadge>

        <ActionGroup>
          <ActionButton
            onClick={() => handleActionClick('delete')}
            title="Delete selected"
          >
            <Trash2 />
            <span>Delete</span>
          </ActionButton>

          <ActionButton
            onClick={() => handleActionClick('reassign')}
            title="Reassign tasks"
          >
            <Users2 />
            <span>Reassign</span>
          </ActionButton>

          <ActionButton
            onClick={onEditStatus}
            title="Edit status"
          >
            <Edit3 />
            <span>Edit Status</span>
          </ActionButton>

        
          <div style={{ position: 'relative' }}>
    <ActionButton
      onClick={() => handleActionClick('updateDueDate')}
      title="Update due date"
    >
      <Calendar />
      <span>Due Date</span>
    </ActionButton>

    {showDatePicker && (
      <DatePickerContainer>
        <DatePicker
          showTime
          format="YYYY-MM-DD"
          onChange={handleDateChange}
          onOpenChange={(open) => !open && setShowDatePicker(false)}
          autoFocus
        />
      </DatePickerContainer>
    )}
  </div>

          <ActionButton
            onClick={() => handleActionClick('complete')}
            title="Mark as complete"
          >
            <CheckSquare />
            <span>Complete</span>
          </ActionButton>

          <ActionButton
            onClick={onCopyToList}
            title="Copy to list"
          >
            <Copy />
            <span>Copy</span>
          </ActionButton>

          <ActionButton
            onClick={onAutomation}
            title="Apply automation"
          >
            <Zap />
            <span>Automation</span>
          </ActionButton>

          <ActionButton
            onClick={onClearSelection}
            title="Clear selection"
          >
            <X />
            <span>Clear</span>
          </ActionButton>
        </ActionGroup>

        
      </ActionBar>

      {confirmation.isOpen && (
  <ModalOverlay>
    <ModalContent>
      <ModalTitle>{getActionTitle(confirmation.action)}</ModalTitle>
      <ModalMessage>{confirmation.message}</ModalMessage>
      
      {/* Reassignment Select */}
      {confirmation.action === 'reassign' && (
        <SelectWrapper>
          <select
            value={confirmation.selectedEmployee || ''}
            onChange={(e) => setConfirmation(prev => ({
              ...prev,
              selectedEmployee: e.target.value
            }))}
            className="form-select"
          >
            <option value="">Select Employee</option>
            {employees.map((emp: Employee) => (
  <option key={emp.value} value={emp.value}>
    {emp.label}
  </option>
))}
          </select>
        </SelectWrapper>
      )}

      {/* Status Select */}
      {confirmation.action === 'editStatus' && (
        <SelectWrapper>
          <select
            value={confirmation.selectedStatus || ''}
            onChange={(e) => setConfirmation(prev => ({
              ...prev,
              selectedStatus: e.target.value
            }))}
            className="form-select"
          >
            <option value="">Select Status</option>
            {statusOptions.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </SelectWrapper>
      )}

      {/* List Select */}
      {confirmation.action === 'copyToList' && (
        <SelectWrapper>
          <select
            value={confirmation.selectedList || ''}
            onChange={(e) => setConfirmation(prev => ({
              ...prev,
              selectedList: e.target.value
            }))}
            className="form-select"
          >
            <option value="">Select List</option>
            {listOptions.map((list) => (
              <option key={list.value} value={list.value}>
                {list.label}
              </option>
            ))}
          </select>
        </SelectWrapper>
      )}

      {/* Automation Select */}
      {confirmation.action === 'automation' && (
        <SelectWrapper>
          <select
            value={confirmation.selectedAutomation || ''}
            onChange={(e) => setConfirmation(prev => ({
              ...prev,
              selectedAutomation: e.target.value
            }))}
            className="form-select"
          >
            <option value="">Select Automation</option>
            {automationOptions.map((automation) => (
              <option key={automation.value} value={automation.value}>
                {automation.label}
              </option>
            ))}
          </select>
        </SelectWrapper>
      )}

      

      {/* Action Buttons */}
      <ModalActions>
        <Button
          onClick={() => setConfirmation({
            isOpen: false,
            action: '',
            message: '',
            selectedEmployee: undefined,
            selectedStatus: undefined,
            selectedList: undefined,
            selectedAutomation: undefined
          })}
        >
          Cancel
        </Button>


        {/* Show Confirm button only for non-datepicker actions */}
        {confirmation.action !== 'updateDueDate' && (
          <Button
            isWarning={confirmation.action === 'delete'}
            disabled={
              (confirmation.action === 'reassign' && !confirmation.selectedEmployee) ||
              (confirmation.action === 'editStatus' && !confirmation.selectedStatus) ||
              (confirmation.action === 'copyToList' && !confirmation.selectedList) ||
              (confirmation.action === 'automation' && !confirmation.selectedAutomation)
            }
            onClick={handleConfirm}
          >
            {getActionTitle(confirmation.action)}
          </Button>
        )}
      </ModalActions>
      
    </ModalContent>
  </ModalOverlay>
)}
    </>
  );
};

export default TaskBulkActionsBar;